import React, { useState, useEffect, useMemo } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  TextField,
  Button,
  Tooltip,
  Icon,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import toast from "react-hot-toast";
import { InfoOutlined } from "@mui/icons-material";

const PrincipleQuestions = ({
  questions,
  principle,
  isLoading,
  onAnswerSubmitted,
}) => {
  const [answers, setAnswers] = useState({});
  const [comments, setComments] = useState({});
  const [acceptedFiles, setAcceptedFiles] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [isCommentMandatory, setIsCommentMandatory] = useState(false);
  const [openFinalizarModal, setOpenFinalizarModal] = useState(false);
  const [loadingFinalizarModal, setLoadingFinalizarModal] = useState(false);
  const [showFinalizarBtn, setShowFinalizarBtn] = useState(false);

  // const [questionsData, setQuestionsData] = useState([]);
  const [principleData, setprincipleData] = useState([]);
  const [loadingForAnswerSubmittion, setLoadingForAnswerSubmittion] =
    useState(false);
  // Initialize answers and comments from the question data
  useEffect(() => {
    if (principle?.id && Array.isArray(questions)) {
      setExpanded(null);
      setAnswers({});
      setComments({});
    }
  }, [principle?.id, questions]);

  useEffect(() => {
    if (principle?.id && Array.isArray(questions)) {
      const initialAnswers = {};
      const initialComments = {};

      questions.forEach((question) => {
        const answer = question.answer || {};

        if (question.question_type === "OM") {
          initialAnswers[question.id] = Array.isArray(answer.choices)
            ? answer.choices
            : [];
        } else if (question.question_type === "SN") {
          // Handle SN type explicitly
          initialAnswers[question.id] = answer.yes_no_answer || "";
        }

        initialComments[question.id] = answer.comment || "";
      });

      setAnswers(initialAnswers);
      setComments(initialComments);
      setExpanded(null);
    }
  }, [questions, principle?.id]); // Add questions to dependencies

  // Dropzone configuration
  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      onDrop: (files) => setAcceptedFiles(files),
      onDropRejected: (fileRejections) => {
        const hasTooLargeFile = fileRejections.some(({ errors }) =>
          errors?.some((error) => error.code === "file-too-large")
        );

        toast.error(
          hasTooLargeFile
            ? "El archivo supera el tamaño máximo permitido de 200 MB."
            : "El archivo no cumple con el formato permitido. Solo se permiten PDF o PNG de hasta 200 MB."
        );
      },
      accept: {
        "application/pdf": [".pdf"],
        "image/png": [".png"],
      },
      maxSize: 200 * 1024 * 1024, // 200 MB in bytes
    });

  const baseStyle = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "30px",
    margin: "20px 0px",
    borderWidth: 2,
    borderRadius: "12px",
    borderColor: "grey",
    backgroundColor: "white",
    borderStyle: "dashed",
    color: "#bdbdbd",
    outline: "none",
    transition: "border .24s ease-in-out",
  };

  const focusedStyle = {
    borderColor: "#2196f3",
  };

  const acceptStyle = {
    borderColor: "#00e676",
  };

  const rejectStyle = {
    borderColor: "#ff1744",
  };

  const style = useMemo(() => {
    return {
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    };
  }, [isFocused, isDragAccept, isDragReject]);

  // Handle comment change
  const handleCommentChange = (questionId, comment) => {
    setComments((prevComments) => ({
      ...prevComments,
      [questionId]: comment,
    }));
  };
  // Handle answer selection
  const handleAnswerChange = (questionId, answer) => {
    setIsCommentMandatory(answer === "S" ? true : false);
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: answer,
    }));
  };
  // Handle accordion expansion
  const handleAccordionChange = (questionId) => (event, isExpanded) => {
    setExpanded(isExpanded ? questionId : null);
    setAcceptedFiles([]);
  };
  const fetchEditionCompletionStatus = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/principal/edition-completion-status`,
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );

      console.log(
        "Edition Completion Status:",
        response.data?.data?.show_finalizar_button
      );
      if (response.data?.data?.show_finalizar_button) {
        setShowFinalizarBtn(true);
      } else {
        setShowFinalizarBtn(false);
      }
    } catch (error) {
      console.error("Failed to fetch edition completion status:", error);
      setShowFinalizarBtn(false);
    }
  };
  useEffect(() => {
    fetchEditionCompletionStatus();
  }, []);
  const submitSingleQuestion = async (principle, questionId, question) => {
    setLoadingForAnswerSubmittion(true);
    const token = localStorage.getItem("auth_token");

    if (!token) {
      toast.error("No se encontró token de autenticación.");
      setLoadingForAnswerSubmittion(false);
      return;
    }
    if (
      question.question_type === "SN" &&
      isCommentMandatory === true &&
      (!comments[questionId] || comments[questionId].trim() === "")
    ) {
      toast.error("Se necesita comentario");
      setLoadingForAnswerSubmittion(false);
      setIsCommentMandatory(false);
      return;
    }
    try {
      // Prepare answer payload
      const answerPayload = {
        comment: comments[questionId] || "",
      };

      // Handle answer type
      const answerType =
        answers[questionId] ||
        (question?.answer?.choices && question.question_type === "OM"
          ? question.answer.choices
          : "");

      // Validate multiple choice selection
      if (
        question.question_type === "OM" &&
        (!Array.isArray(answerType) || answerType.length === 0)
      ) {
        toast.error("Por favor selecciona al menos una opción.");
        return;
      }

      // Add answer to payload
      if (question.question_type === "OM") {
        answerPayload.choices = answerType;
      } else {
        answerPayload.yes_no_answer = answerType;
      }

      // Submit answer
      const { data: answerData } = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/principal/${principle?.id}/questions/${questionId}/answer/`,
        answerPayload,
        { headers: { Authorization: `Token ${token}` } }
      );

      // Handle file upload
      if (acceptedFiles.length > 0 && answerData?.data?.id) {
        const fileFormData = new FormData();
        acceptedFiles.forEach((file) => fileFormData.append("file", file));

        await axios.post(
          `${process.env.REACT_APP_SERVER_URL}/api/principal/${principle?.id}/questions/${questionId}/answer/${answerData.data.id}/upload/`,
          fileFormData,
          {
            headers: {
              Authorization: `Token ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        toast.success("Archivos subidos exitosamente");
      }

      toast.success("Respuesta enviada correctamente");

      // Clear states and trigger parent refresh
      setExpanded(null);
      setAcceptedFiles([]);
      onAnswerSubmitted();
      fetchEditionCompletionStatus(); // Single refresh trigger for parent
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(
        error.response?.data?.message || "Error al enviar la respuesta"
      );
    } finally {
      setLoadingForAnswerSubmittion(false);
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          textAlign: "center",
          padding: "0 20px",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (!questions || questions.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          textAlign: "center",
          padding: "0 20px",
        }}
      >
        <p>No hay preguntas disponibles para este principio.</p>
      </div>
    );
  }

  const getFilenameFromURL = (url) => {
    try {
      const cleanURL = url.split("?")[0];
      const filename = cleanURL.substring(cleanURL.lastIndexOf("/") + 1);
      return decodeURIComponent(filename);
    } catch {
      return "Archivo";
    }
  };
  console.log("676767", questions);
  const processedQuestions = questions.map((question) => {
    const selected = answers?.[question.id] ?? question.answer?.choices ?? [];

    const choices = question.choices ?? [];
    const lastChoice = choices[choices.length - 1]; // Last option
    const isLastChoiceSelected = selected.includes(lastChoice?.id);

    const isOtherSelected = choices.find(
      (c) => c.text.trim().toLowerCase() === "other" && selected.includes(c.id)
    );

    const commentRequired = !!isOtherSelected;
    const commentText = comments?.[question.id]?.trim();
    const showError = commentRequired && !commentText;

    const isSingleAnswer =
      question.question_type === "OM" && question.single_answer === true;

    return {
      question,
      selected,
      commentRequired,
      commentText,
      showError,
      isSingleAnswer,
      isLastChoiceSelected,
      lastChoiceId: lastChoice?.id,
    };
  });

  const handleDone = (principle, questionId, question) => {
    let hasError = false;

    questions.forEach((question) => {
      const selectedChoices = answers?.[question.id] ?? [];
      const otherChoice = question.choices.find((c) => c.text === "Otros");

      const isOtherSelected =
        otherChoice && selectedChoices.includes(otherChoice.id);
      const commentValue = comments?.[question.id]?.trim();

      if (isOtherSelected && !commentValue) {
        hasError = true;
        toast.error("Por favor, proporcione un comentario para esta pregunta.");
      }
    });

    if (hasError) return;

    // Proceed with submission logic
    submitSingleQuestion(principle, questionId, question); // <- your existing submission logic
  };
  const onFinishHandler = async () => {
    setOpenFinalizarModal(true);
  };
  const handleConfirm = async () => {
    try {
      setLoadingFinalizarModal(true);
      const token = localStorage.getItem("auth_token");

      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/principal/mark-edition-submitted`,
        {},
        { headers: { Authorization: `Token ${token}` } }
      );

      toast.success("Proceso finalizado con éxito");
      setOpenFinalizarModal(false);
      fetchEditionCompletionStatus();
    } catch (error) {
      console.error(error);
      toast.error("Error al finalizar el proceso");
    } finally {
      setLoadingFinalizarModal(false);
    }
  };

  return (
    <>
      <div style={{ margin: "30px" }}>
        {processedQuestions.map(
          ({
            question,
            selected,
            commentRequired,
            commentText,
            showError,
            isSingleAnswer,
            isNingunoSelected,
            isLastChoiceSelected,
            lastChoiceId,
          }) => (
            <Accordion
              key={question.id}
              expanded={expanded === question.id}
              onChange={handleAccordionChange(question.id)}
              style={{ marginBottom: "10px" }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                style={{ backgroundColor: "#fff3f3" }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  {/* Checkbox with green background when checked */}
                  <Typography style={{ fontSize: "20px" }}>
                    <strong>Pregunta {question?.sorting_index}</strong>
                  </Typography>
                  <Checkbox
                    checked={
                      !!answers[question.id]?.length ||
                      question?.answer?.choices?.length
                    }
                    disabled
                    style={{
                      padding: 0,
                      color: "#8ebd30",
                    }}
                  />
                </div>
              </AccordionSummary>
              <AccordionDetails style={{ padding: "10px 30px" }}>
                <div style={{ display: "flex" }}>
                  <div>{question?.question}</div>

                  <Tooltip
                    placement="top-start"
                    title={
                      question?.help_text ||
                      "No additional information available."
                    }
                  >
                    <Icon size="small">
                      <InfoOutlined fontSize="small" />
                    </Icon>
                  </Tooltip>
                </div>
                {/* Render checkboxes for multiple-choice questions */}
                {question.question_type === "OM" ? (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {question.choices.map((choice, index) => {
                      const lastChoice =
                        question.choices[question.choices.length - 1];
                      const isLastChoice = choice.id === lastChoice.id;
                      const isLastChoiceSelected = selected.includes(
                        lastChoice.id
                      );
                      const isCurrentChoiceSelected = selected.includes(
                        choice.id
                      );

                      const isDisabled =
                        // If last option is selected, disable all others
                        (isLastChoiceSelected && !isLastChoice) ||
                        // If any other is selected, disable last option
                        (!isLastChoiceSelected &&
                          selected.length > 0 &&
                          !isCurrentChoiceSelected &&
                          isLastChoice) ||
                        // For single answer: disable all others except the selected one
                        (isSingleAnswer &&
                          selected.length > 0 &&
                          !isCurrentChoiceSelected);

                      return (
                        <FormControlLabel
                          key={choice.id}
                          control={
                            <Checkbox
                              checked={isCurrentChoiceSelected}
                              disabled={isDisabled}
                              onChange={(e) => {
                                let updated = [];

                                if (isSingleAnswer) {
                                  updated = e.target.checked ? [choice.id] : [];
                                } else {
                                  updated = e.target.checked
                                    ? [...new Set([...selected, choice.id])]
                                    : selected.filter((id) => id !== choice.id);
                                }

                                setAnswers((prev) => ({
                                  ...prev,
                                  [question.id]: updated,
                                }));
                              }}
                            />
                          }
                          label={choice.text}
                        />
                      );
                    })}
                  </div>
                ) : question.question_type === "SN" ? (
                  <RadioGroup
                    style={{ display: "flex", flexDirection: "row" }}
                    value={answers[question.id] || ""}
                    onChange={(e) =>
                      handleAnswerChange(question.id, e.target.value)
                    }
                  >
                    <FormControlLabel
                      value="S"
                      control={<Radio />}
                      label="Si"
                    />
                    <FormControlLabel
                      value="N"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                ) : (
                  <Typography>Unsupported question type.</Typography>
                )}
                <div
                  style={{
                    border: "1px solid #1976D2",
                    backgroundColor: "#E3F2FD",
                    color: "#1976D2",
                    padding: "10px 15px",
                    borderRadius: "5px",
                    fontSize: "14px",
                    fontFamily: "Arial, sans-serif",
                  }}
                >
                  <h5>Indicación de la contestación</h5>
                  <p style={{ margin: "5px 0", color: "#1565C0" }}>
                    {question?.description
                      ? question?.description
                      : "Aún no hay texto de ayuda"}
                  </p>
                </div>
                {/* Comment box for additional input */}
                <div style={{ marginTop: "10px" }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={comments[question.id] || ""}
                    onChange={(e) =>
                      handleCommentChange(question.id, e.target.value)
                    }
                    placeholder="Agregue comentarios segun su respuesta"
                    variant="outlined"
                    error={showError}
                    helperText={
                      showError
                        ? "Comentario requerido si selecciona 'Other'"
                        : ""
                    }
                  />
                </div>
                {/* Dropzone for file upload */}
                <div style={{ marginTop: "10px" }}>
                  <div {...getRootProps({ className: "dropzone", style })}>
                    <input {...getInputProps()} />
                    <div style={{ display: "flex" }}>
                      <p className="description-font" style={{ color: "grey" }}>
                        Anexar documentos relacionados con la pregunta
                      </p>
                      <img
                        className="dropzone-image"
                        style={{
                          width: "40px",
                          height: "30px",
                          marginLeft: "20px",
                        }}
                        src="/attachment.png"
                        alt="attachment"
                      />
                    </div>
                  </div>
                  {acceptedFiles.length > 0 && (
                    <aside>
                      <h4>Selected file:</h4>
                      <ul>
                        {acceptedFiles.map((file) => (
                          <li key={file.path}>
                            {file.path} - {file.size} bytes
                          </li>
                        ))}
                      </ul>
                    </aside>
                  )}
                </div>
                Documentos ya seleccionados:
                <div>
                  {question?.answer?.reference_documents?.map(
                    (fileData, index) => (
                      <div key={index}>
                        <a
                          href={fileData.file}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            textDecoration: "underline",
                            color: "#007bff",
                          }}
                        >
                          📎 {getFilenameFromURL(fileData.file)}
                        </a>
                      </div>
                    )
                  )}
                </div>
                {/* Submit button for individual questions */}
                <div style={{ marginTop: "20px", textAlign: "center" }}>
                  {loadingForAnswerSubmittion ? (
                    <div
                      style={{
                        fontSize: "16px",
                        color: "#555",
                        marginTop: "10px",
                      }}
                    >
                      ⏳ Guardando respuesta...
                    </div>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={() =>
                        handleDone(principle, question.id, question)
                      }
                      sx={{
                        backgroundColor: "#000000",
                        color: "#FFFFFF !important",
                        fontSize: "16px",
                        padding: "8px 20px",
                        borderRadius: "15px",
                        textTransform: "none",
                        fontWeight: "none !important",
                      }}
                    >
                      Guardar respuesta
                    </Button>
                  )}
                </div>
              </AccordionDetails>
            </Accordion>
          )
        )}
      </div>
      {showFinalizarBtn && (
        <div className="tool-guide_button-container">
          <button className="tool-guide_button" onClick={onFinishHandler}>
            {" "}
            FINALIZAR
          </button>
        </div>
      )}
      <Dialog
        open={openFinalizarModal}
        onClose={() => setOpenFinalizarModal(false)}
      >
        <DialogTitle>
          {" "}
          Está seguro de querer finalizar este cuestionario?{" "}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Una vez finalizado no tendrá la opción de editarlo.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleConfirm}
            style={{ color: "red", textTransform: "none" }}
            disabled={loadingFinalizarModal}
          >
            {loadingFinalizarModal ? "Finalizando..." : "Finalizar"}
          </Button>

          <Button
            onClick={() => setOpenFinalizarModal(false)}
            style={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PrincipleQuestions;
